/**
 * Collection Hub Product Card Functionality
 * Handles variant selection and add to cart
 */

(function() {
  'use strict';

  // Store product data for each card
  const productDataCache = {};

  // Initialize product cards
  function initProductCards() {
    // Handle variant selection changes
    document.addEventListener('change', function(e) {
      if (e.target.classList.contains('chub-variant-select')) {
        handleVariantChange(e.target);
      }
    });

    // Handle add to cart clicks
    document.addEventListener('click', function(e) {
      if (e.target.closest('.chub-add-to-cart-btn')) {
        e.preventDefault();
        const btn = e.target.closest('.chub-add-to-cart-btn');
        handleAddToCart(btn);
      }
    });
  }

  // Handle variant selection change
  function handleVariantChange(selectElement) {
    const productId = selectElement.getAttribute('data-product-id');
    const card = selectElement.closest('.chub-product-card');
    
    if (!card || !productId) return;

    // Get all selected options
    const selectedOptions = {};
    card.querySelectorAll('.chub-variant-select').forEach(select => {
      const optionIndex = select.getAttribute('data-option-index');
      const value = select.value;
      if (value) {
        selectedOptions[optionIndex] = value;
      }
    });

    // If we have product data, update price and image
    if (productDataCache[productId]) {
      const variant = findVariantByOptions(productDataCache[productId], selectedOptions);
      if (variant) {
        updateCardPrice(card, variant);
        updateCardImage(card, variant, productDataCache[productId]);
      }
    } else {
      // Try to fetch product data
      fetchProductData(productId, card);
    }
  }

  // Fetch product data
  function fetchProductData(productId, card) {
    const productHandle = card.getAttribute('data-product-handle');
    if (!productHandle) return Promise.resolve();

    return fetch(`/products/${productHandle}.js`)
      .then(response => response.json())
      .then(product => {
        // Ensure product has media array for image matching
        if (!product.media && product.images) {
          product.media = product.images.map((img, index) => ({
            src: typeof img === 'string' ? img : (img.src || img),
            media_type: 'image',
            alt: product.variants && product.variants[index] ? product.variants[index].option1 : ''
          }));
        }
        productDataCache[productId] = product;
        const selectedOptions = getSelectedOptions(card);
        const variant = findVariantByOptions(product, selectedOptions);
        if (variant) {
          updateCardPrice(card, variant);
          updateCardImage(card, variant, product);
        }
        return product;
      })
      .catch(error => {
        console.error('Error fetching product data:', error);
        return null;
      });
  }

  // Get selected options from card
  function getSelectedOptions(card) {
    const selectedOptions = {};
    card.querySelectorAll('.chub-variant-select').forEach(select => {
      const optionIndex = select.getAttribute('data-option-index');
      const value = select.value;
      if (value) {
        selectedOptions[optionIndex] = value;
      }
    });
    return selectedOptions;
  }

  // Find variant by selected options
  function findVariantByOptions(product, selectedOptions) {
    if (!product || !product.variants || product.variants.length === 0) {
      return null;
    }

    // If no options selected, return first available variant
    if (Object.keys(selectedOptions).length === 0) {
      return product.variants.find(v => v.available) || product.variants[0];
    }

    // Find matching variant
    const matchingVariant = product.variants.find(variant => {
      return Object.keys(selectedOptions).every(optionIndex => {
        const selectedValue = selectedOptions[optionIndex];
        const variantOption = variant.options && variant.options[parseInt(optionIndex)];
        return variantOption === selectedValue;
      });
    });

    return matchingVariant || product.variants[0];
  }

  // Update card price
  function updateCardPrice(card, variant) {
    const priceEl = card.querySelector('.chub-price');
    const comparePriceEl = card.querySelector('.chub-compare-price');
    const addBtn = card.querySelector('.chub-add-to-cart-btn');

    if (priceEl && variant) {
      priceEl.textContent = formatMoney(variant.price);
    }

    if (comparePriceEl && variant) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        comparePriceEl.textContent = formatMoney(variant.compare_at_price);
        comparePriceEl.style.display = 'inline';
      } else {
        comparePriceEl.style.display = 'none';
      }
    }

    // Update add to cart button based on availability
    if (addBtn) {
      if (variant.available === false || variant.available === 0) {
        addBtn.disabled = true;
        addBtn.textContent = 'SOLD OUT';
      } else {
        addBtn.disabled = false;
        addBtn.textContent = 'ADD TO CART';
      }
    }
  }

  // Update card image based on variant
  function updateCardImage(card, variant, product) {
    if (!card || !variant || !product) return;

    const imgEl = card.querySelector('.chub-product-card-img');
    if (!imgEl) return;

    // Check if variant has featured_image
    let imageSrc = null;
    
    if (variant.featured_image) {
      // Variant has its own featured image
      imageSrc = typeof variant.featured_image === 'string' 
        ? variant.featured_image 
        : (variant.featured_image.src || variant.featured_image);
    } else if (product.images && product.images.length > 0) {
      // Try to find image matching variant's option1 (color)
      if (variant.option1 && product.media) {
        const matchingMedia = product.media.find(media => 
          media.media_type === 'image' && 
          (media.alt === variant.option1 || media.alt?.includes(variant.option1))
        );
        if (matchingMedia) {
          imageSrc = typeof matchingMedia === 'string' ? matchingMedia : (matchingMedia.src || matchingMedia);
        }
      }
      
      // Fallback to first product image
      if (!imageSrc) {
        imageSrc = typeof product.images[0] === 'string' 
          ? product.images[0] 
          : (product.images[0].src || product.images[0]);
      }
    } else if (product.featured_image) {
      // Use product featured image
      imageSrc = typeof product.featured_image === 'string' 
        ? product.featured_image 
        : (product.featured_image.src || product.featured_image);
    }

    // Update image if we found one
    if (imageSrc) {
      imgEl.src = imageSrc;
      // Add fade effect
      imgEl.style.opacity = '0.7';
      imgEl.style.transition = 'opacity 0.3s ease';
      setTimeout(() => {
        imgEl.style.opacity = '1';
      }, 150);
    }
  }

  // Handle add to cart
  function handleAddToCart(btn) {
    const card = btn.closest('.chub-product-card');
    const productId = btn.getAttribute('data-product-id');
    const productHandle = btn.getAttribute('data-product-handle');
    
    if (!card || !productHandle) return;

    // Get quantity first
    const quantitySelect = card.querySelector('.chub-quantity-select');
    const quantity = quantitySelect ? parseInt(quantitySelect.value) || 1 : 1;

    // Get selected variant
    const selectedOptions = getSelectedOptions(card);
    let variant = null;

    // Try to get variant from cache
    if (productDataCache[productId]) {
      variant = findVariantByOptions(productDataCache[productId], selectedOptions);
    }

    // If no variant found, fetch product data first
    if (!variant) {
      fetchProductData(productId, card).then((product) => {
        if (product) {
          variant = findVariantByOptions(product, selectedOptions);
          if (variant) {
            // Check if variant is available
            if (variant.available === false || variant.available === 0) {
              // Update button to show SOLD OUT
              btn.disabled = true;
              btn.textContent = 'SOLD OUT';
              btn.classList.remove('adding');
              return;
            }
            addToCartRequest(variant, btn, quantity);
          } else {
            alert('Please select all options');
            btn.disabled = false;
            btn.classList.remove('adding');
            btn.textContent = 'ADD TO CART';
          }
        } else {
          alert('Error loading product. Please try again.');
          btn.disabled = false;
          btn.classList.remove('adding');
          btn.textContent = 'ADD TO CART';
        }
      });
      return;
    }

    // Check if variant is available
    if (variant.available === false || variant.available === 0) {
      // Update button to show SOLD OUT
      btn.disabled = true;
      btn.textContent = 'SOLD OUT';
      btn.classList.remove('adding');
      return;
    }

    // Add to cart
    addToCartRequest(variant, btn, quantity);
  }

  // Add to cart request
  function addToCartRequest(variant, btn, quantity = 1) {
    const originalText = btn.textContent;
    btn.disabled = true;
    btn.classList.add('adding');
    btn.textContent = 'ADDING...';

    // Use FormData format (same as product-form.js)
    const formData = new FormData();
    formData.append('id', variant.id);
    formData.append('quantity', quantity);

    // Get sections to render
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
      const sections = cartDrawer.getSectionsToRender().map((section) => section.id);
      formData.append('sections', sections.join(','));
      formData.append('sections_url', window.location.pathname);
    }

    fetch('/cart/add.js', {
      method: 'POST',
      body: formData
    })
    .then(response => {
      if (!response.ok) {
        return response.text().then(text => {
          throw new Error(`HTTP ${response.status}: ${text}`);
        });
      }
      return response.json();
    })
    .then(data => {
      // Check for errors (Shopify returns {status: 422, description: "..."} on error)
      if (data.status) {
        alert(data.description || 'Error adding to cart');
        btn.textContent = originalText;
        btn.disabled = false;
        btn.classList.remove('adding');
        return;
      }

      // Trigger cart update event
      document.dispatchEvent(new CustomEvent('cart:updated'));
      
      // Fetch updated cart sections and open drawer
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        fetch('/?sections=cart-drawer,cart-icon-bubble')
          .then(response => response.json())
          .then(sections => {
            if (typeof cartDrawer.renderContents === 'function') {
              cartDrawer.renderContents({
                sections: sections
              });
            } else {
              cartDrawer.open();
            }
          })
          .catch(err => {
            console.error('Error fetching cart sections:', err);
            if (typeof cartDrawer.open === 'function') {
              cartDrawer.open();
            }
          });
      }
      
      // Show success
      btn.textContent = 'ADDED!';
      btn.classList.remove('adding');
      
      setTimeout(() => {
        btn.textContent = originalText;
        btn.disabled = false;
      }, 1500);
    })
    .catch(error => {
      console.error('Error adding to cart:', error);
      btn.textContent = 'ERROR - TRY AGAIN';
      btn.classList.remove('adding');
      
      setTimeout(() => {
        btn.textContent = originalText;
        btn.disabled = false;
      }, 2000);
    });
  }

  // Format money
  function formatMoney(cents) {
    if (typeof cents !== 'number') {
      cents = parseFloat(cents) || 0;
    }
    
    // Use Shopify's money format if available
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }
    
    // Fallback formatting
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProductCards);
  } else {
    initProductCards();
  }

  // Re-initialize on section load (Shopify theme editor)
  document.addEventListener('shopify:section:load', function() {
    initProductCards();
  });

})();

